// useNotifications — backing data for the topbar bell. Fetches on
// mount, subscribes to the SSE channel for live updates, and exposes
// helpers for mark-read / dismiss / mark-all-read.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, type Notification, type NotificationSummary } from "./rpc";
import { sse } from "./sse";

export interface UseNotifications {
  items: Notification[];
  summary: NotificationSummary;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: number) => Promise<void>;
}

export function useNotifications(): UseNotifications {
  const [items, setItems] = useState<Notification[]>([]);
  const [summary, setSummary] = useState<NotificationSummary>({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.notificationsList({ limit: 50 }),
        api.notificationsSummary(),
      ]);
      setItems(list);
      setSummary(sum);
    } catch {
      // Stay silent on first-paint failures — the bell with a 0 badge
      // is a perfectly fine fallback. Real refresh on reconnect picks
      // it up via the SSE subscription below.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // SSE: cross-device sync for created / read / dismissed events.
  // The server emits these on every mutation; we apply them locally
  // so a notification dismissed on one tab disappears on others
  // without waiting for a manual refresh.
  useEffect(() => {
    return sse.on((ev: any) => {
      if (ev?.type === "notifications:created" && ev.item) {
        setItems((prev) => {
          if (prev.some((x) => x.id === ev.item.id)) return prev;
          return [ev.item, ...prev];
        });
        setSummary((prev) => ({ total: prev.total + 1, unread: prev.unread + 1 }));
        // Android wrapper: fire an OS notification so the user sees
        // this even when the WebView is backgrounded or on a different
        // tab. No-op on non-Android — `window.VultureAlarms` is only
        // injected by the wrapper. Tag uses dedupKey so re-runs of the
        // server cron collapse to a single OS notification, matching
        // the bell's in-app dedup.
        try {
          const bridge = (window as any).VultureAlarms;
          if (bridge?.notifyNow) {
            const { title, body } = formatForOs(ev.item);
            bridge.notifyNow(JSON.stringify({
              title, body,
              tag: ev.item.dedupKey || `notif-${ev.item.id}`,
              // Hash routes back to the right section on tap. Native
              // MainActivity reads this from the deep_link intent
              // extra and sets window.location.hash.
              deepLink: ev.item.kind.startsWith("pantry.") ? "#notif=pantry" : null,
            }));
          }
        } catch { /* bridge absent or threw — in-app bell still works */ }
      } else if (ev?.type === "notifications:updated" && ev.item) {
        setItems((prev) => prev.map((x) => x.id === ev.item.id ? ev.item : x));
        // Bell-side summary delta — going from unread to read decreases
        // the unread count by 1; everything else is a no-op for badge
        // purposes. Use the absolute counts from refresh() if precision
        // matters; this delta is fine for typical use.
        setSummary((prev) => ({
          ...prev,
          unread: Math.max(0, prev.unread - (ev.item.readAt ? 1 : 0)),
        }));
      } else if (ev?.type === "notifications:dismissed" && ev.id != null) {
        setItems((prev) => prev.filter((x) => x.id !== ev.id));
        setSummary((prev) => ({
          total: Math.max(0, prev.total - 1),
          unread: Math.max(0, prev.unread - 1),
        }));
      } else if (ev?.type === "notifications:bulk_read") {
        setItems((prev) => prev.map((x) => x.readAt ? x : { ...x, readAt: new Date().toISOString() }));
        setSummary((prev) => ({ ...prev, unread: 0 }));
      }
    });
  }, []);

  const markRead = useCallback(async (id: number) => {
    // Optimistic: flip readAt locally so the badge ticks down
    // immediately. SSE round-trip reconciles if anything raced.
    setItems((prev) => prev.map((x) => x.id === id && !x.readAt ? { ...x, readAt: new Date().toISOString() } : x));
    setSummary((prev) => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    try { await api.notificationsMarkRead(id); }
    catch { /* refresh() will reconcile on next focus */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((x) => x.readAt ? x : { ...x, readAt: new Date().toISOString() }));
    setSummary((prev) => ({ ...prev, unread: 0 }));
    try { await api.notificationsMarkAllRead(); }
    catch { /* same */ }
  }, []);

  const dismiss = useCallback(async (id: number) => {
    const removed = items.find((x) => x.id === id);
    setItems((prev) => prev.filter((x) => x.id !== id));
    setSummary((prev) => ({
      total: Math.max(0, prev.total - 1),
      unread: Math.max(0, prev.unread - (removed && !removed.readAt ? 1 : 0)),
    }));
    try { await api.notificationsDismiss(id); }
    catch { /* refresh on next focus */ }
  }, [items]);

  return { items, summary, loading, refresh, markRead, markAllRead, dismiss };
}

// Format a notification for an OS-level Android notification. Mirrors
// the in-app bell's renderNotification() copy but flattened to plain
// title/body strings since the OS notification surface doesn't accept
// the same JSX shape. Kept here next to the bridge call so adding a
// new producer kind only requires one place to update.
function formatForOs(n: Notification): { title: string; body: string } {
  const p = n.payload || {};
  if (n.kind === "pantry.expiring") {
    return {
      title: `${p.name || "Pantry item"} expires soon`,
      body: p.expires ? `${p.qty || ""} · expires ${p.expires}`.trim() : (p.qty || ""),
    };
  }
  if (n.kind === "pantry.expired") {
    return {
      title: `${p.name || "Pantry item"} expired`,
      body: p.expires ? `${p.qty || ""} · was ${p.expires}`.trim() : (p.qty || ""),
    };
  }
  if (n.kind === "kidchat.flagged") {
    return {
      title: `Flagged in ${p.kidUserId || "a kid"}'s chat`,
      body: (Array.isArray(p.categories) ? p.categories.join(", ") : "") + (p.snippet ? ` — "${p.snippet}"` : ""),
    };
  }
  if (n.kind === "chore.pending_approval") {
    return {
      title: `${p.completedByName || p.completedByUserId || "Someone"} finished "${p.choreName || "a chore"}"`,
      body: "Tap to review and grant the stars.",
    };
  }
  return { title: n.kind, body: JSON.stringify(p).slice(0, 120) };
}
